'use client';

import { useEffect } from 'react';
import { reportClientError } from '@/lib/reportClientError';
import { recoverFromChunkError } from '@/lib/chunkReload';

// Replaces the root layout when it (or the root error boundary) throws, so it
// must render its own <html>/<body> and cannot rely on global CSS loading.
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error(error);
    reportClientError(error, 'global-error-boundary');
    // Six of the ten chunk errors logged on 2026-08-20 arrived here rather than
    // in the route boundary, so the recovery has to live in both.
    void recoverFromChunkError(error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fbfaf6',
          color: '#201d15',
          fontFamily:
            'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          textAlign: 'center',
          padding: '24px',
        }}
      >
        <div style={{ maxWidth: '28rem' }}>
          <h1
            style={{
              margin: 0,
              fontSize: '1.75rem',
              fontWeight: 600,
              letterSpacing: '-0.01em',
            }}
          >
            GoReal hit an unexpected error
          </h1>
          <p style={{ marginTop: '12px', marginBottom: 0, color: 'rgb(32 29 21 / 65%)' }}>
            Something broke on our side. Try again, or head back to the homepage.
          </p>
          <div
            style={{
              marginTop: '28px',
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              onClick={reset}
              style={{
                appearance: 'none',
                border: 'none',
                borderRadius: '12px',
                backgroundColor: '#a41f2e',
                color: '#fbfaf6',
                padding: '12px 24px',
                fontSize: '0.9375rem',
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            {/* Plain <a> on purpose: the app shell has crashed, so a full page
                load is the reliable way home (next/link needs the router). */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a href="/" style={{ color: '#821723', fontWeight: 600 }}>
              Go home
            </a>
          </div>
        </div>
      </body>
    </html>
  );
}
